import { action } from '@reatom/framework'
import { useAction, useAtom } from '@reatom/npm-react'
import { memo, useState } from 'react'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import {
  finalStretchExploreFiltersAtom,
  graduatedExploreFiltersAtom,
  newExploreFiltersAtom,
} from '@root/features/explore/atoms'
import { exploreFilterDrawer } from '@root/features/explore/atoms/reatom-explore-filter-drawer'
import { exploreNewPairsModel } from '@root/features/explore/atoms/reatom-explore-new'
import {
  exploreFinalStretchModel,
  exploreGraduatedModel,
} from '@root/features/explore/atoms/reatom-explore-top-gainers'
import { EXPLORE_LIST_PERIOD_MAP } from '@root/features/explore/model/constants'
import { ExploreFeedType } from '@root/features/explore/model/explore-feed-type'

import {
  boardDisplayAtom,
  setBoardDensityAction,
} from '../../model/board-display-atom'

import { BoardNav } from './board-nav'
import { PulseDisplay } from './pulse-display'
import { PulseFilters } from './pulse-filters'
import { PulseFooter } from './pulse-footer'
import { PulseV2Row } from './pulse-v2-row'
import { pulseV2Styles } from './pulse-v2.styles'
import { QuickBuyAmount } from './quick-buy-amount'

import type { Action, Atom, AtomMut } from '@reatom/framework'
import type { PairMetrics, PairModel } from '@root/entities/pair'
import type { ExploreFiltersAtom } from '@root/features/explore/atoms'
import type { Error } from '@root/shared'

const openExploreFiltersAction = action(
  (ctx, filters: ExploreFiltersAtom) =>
    exploreFilterDrawer.exploreFilterDrawer(ctx, filters),
  'openExploreFiltersAction',
)

/** Only the parts of an explore feed model this screen reads. */
type FeedModel = {
  resolvedPairsStateAtom: Atom<
    AsyncStateN.AsyncStateN<Error.UnknownError, Array<PairModel>>
  >
  load: Action<[], unknown>
  paused: AtomMut<boolean>
  buffedCount: Atom<number>
}

/**
 * Pulse V2 — the whole screen, not a panel inside the old one.
 *
 * It owns its chrome: its own bar, its own column heads, its own footer, its
 * own palette. Nothing of the terminal's shell appears, which is the point —
 * the two boards are meant to be judged against each other, and a redesign
 * wearing the old frame proves nothing.
 *
 * The data is V1's, though: the same feed models, the same requests, the same
 * live updates. Only the presentation differs.
 *
 * Design: https://claude.ai/artifact/5DHS184wUvyGV3PGQTbvLS
 */
export const PulseScreen: React.FC = memo(() => {
  const [display] = useAtom(boardDisplayAtom)

  return (
    <Screen>
      <div className="app" data-density={display.density}>
        <div className="glow" />

        <BoardNav active="pulse" />

        <div className="bar">
          <h1>Pulse</h1>
          <div className="chains">
            {CHAINS.map((chain) => (
              <button
                key={chain.label}
                className={chain.on ? 'cn on' : 'cn'}
                type="button"
                aria-label={chain.label}
                style={{ background: chain.tint }}
              >
                <svg
                  viewBox="0 0 24 24"
                  style={{ width: 14, height: 14, fill: chain.color }}
                >
                  {chain.mark}
                </svg>
              </button>
            ))}
          </div>

          <div
            style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <DensitySwitch />
            <DisplayButton />
            <button className="ib" type="button" aria-label="Sound">
              <svg className="io" viewBox="0 0 24 24">
                <path d="M5 9v6h3.5L13 19V5L8.5 9H5Z" />
                <path d="M16.5 9.5a3.6 3.6 0 0 1 0 5" />
              </svg>
            </button>
            <button className="ib" type="button" aria-label="Settings">
              <svg className="io" viewBox="0 0 24 24">
                <path d="M4 8h9M17 8h3M4 16h3M11 16h9" />
                <circle cx="15" cy="8" r="2.2" />
                <circle cx="9" cy="16" r="2.2" />
              </svg>
            </button>
          </div>
        </div>

        <div className="cols">
          {display.columns.new ? (
            <Column
              title="New pairs"
              model={exploreNewPairsModel}
              filters={newExploreFiltersAtom}
              statsPeriod={EXPLORE_LIST_PERIOD_MAP[ExploreFeedType.New]}
            />
          ) : undefined}
          {display.columns.finalStretch ? (
            <Column
              title="Final stretch"
              model={exploreFinalStretchModel}
              filters={finalStretchExploreFiltersAtom}
              statsPeriod={
                EXPLORE_LIST_PERIOD_MAP[ExploreFeedType.FinalStretch]
              }
            />
          ) : undefined}
          {display.columns.migrated ? (
            <Column
              title="Migrated"
              model={exploreGraduatedModel}
              filters={graduatedExploreFiltersAtom}
              statsPeriod={EXPLORE_LIST_PERIOD_MAP[ExploreFeedType.Graduated]}
            />
          ) : undefined}
        </div>

        <PulseFooter />

        <PulseFilters />
      </div>
    </Screen>
  )
})

/** The two-way density switch on the bar. */
const DensitySwitch: React.FC = memo(() => {
  const [display] = useAtom(boardDisplayAtom)
  const setDensity = useAction(setBoardDensityAction)

  return (
    <div className="seg density">
      <button
        className={display.density === 'compact' ? 'on' : undefined}
        type="button"
        onClick={() => setDensity('compact')}
      >
        Compact
      </button>
      <button
        className={display.density === 'regular' ? 'on' : undefined}
        type="button"
        onClick={() => setDensity('regular')}
      >
        Regular
      </button>
    </div>
  )
})

/** Display, and the menu it opens under itself. */
const DisplayButton: React.FC = memo(() => {
  const [open, setOpen] = useState(false)

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        className="pill lit dispbtn"
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <svg className="io" viewBox="0 0 24 24" style={{ color: 'var(--t4)' }}>
          <rect x="3" y="4" width="7" height="16" rx="1.6" />
          <rect x="14" y="4" width="7" height="16" rx="1.6" />
        </svg>
        <span>Display</span>
      </button>
      {open ? <PulseDisplay onClose={() => setOpen(false)} /> : undefined}
    </span>
  )
})

const Column: React.FC<{
  title: string
  model: FeedModel
  filters: ExploreFiltersAtom
  statsPeriod: PairMetrics.MetricsSpan
}> = memo(({ title, model, filters, statsPeriod }) => {
  const [pairsState] = useAtom(model.resolvedPairsStateAtom)
  const [paused, setPaused] = useAtom(model.paused)
  const [buffered] = useAtom(model.buffedCount)
  const load = useAction(model.load)
  const openFilters = useAction(openExploreFiltersAction)

  const count = AsyncStateN.matchWaiting(pairsState, {
    Waiting: () => undefined,
    Error: () => undefined,
    Success: (pairs) => pairs.length,
  })

  const rate = AsyncStateN.matchWaiting(pairsState, {
    Waiting: () => undefined,
    Error: () => undefined,
    Success: (pairs) => arrivalRate(pairs),
  })

  return (
    <section
      className="col"
      /* A column holds still while it is being read, the way the old board
         does — otherwise a row moves out from under the pointer. */
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="hd">
        <h2>{title}</h2>
        <span className="cnt mono">{count ?? ''}</span>
        <span className="rate mono">{rate ?? ''}</span>
        {paused ? (
          <span className="live" style={{ background: 'rgba(224,163,60,.12)' }}>
            <b style={{ color: 'var(--warn)' }}>
              HELD{buffered > 0 ? ` ${buffered}` : ''}
            </b>
          </span>
        ) : (
          <span className="live">
            <i className="dot" />
            <b>LIVE</b>
          </span>
        )}
        <span className="hdr">
          <QuickBuyAmount section="exploreFeeds" shape="pill" />
          <button
            className="ib"
            type="button"
            aria-label={paused ? 'Resume this column' : 'Pause this column'}
            onClick={() => setPaused(!paused)}
            style={paused ? { color: 'var(--warn)' } : undefined}
          >
            {paused ? (
              <svg className="is" viewBox="0 0 24 24">
                <path d="M7 4.5 19 12 7 19.5z" />
              </svg>
            ) : (
              <svg className="is" viewBox="0 0 24 24">
                <path d="M8 5h3v14H8zM13 5h3v14h-3z" />
              </svg>
            )}
          </button>
          <button
            className="ib"
            type="button"
            aria-label="Filters"
            onClick={() => openFilters(filters)}
          >
            <svg
              className="io"
              viewBox="0 0 24 24"
              style={{ width: 13, height: 13 }}
            >
              <path d="M4 8h9M17 8h3M4 16h3M11 16h9" />
              <circle cx="15" cy="8" r="2.2" />
              <circle cx="9" cy="16" r="2.2" />
            </svg>
          </button>
        </span>
      </div>

      <div className="list">
        {AsyncStateN.matchWaiting(pairsState, {
          Waiting: () => <p className="note">Reading the feed…</p>,
          Error: () => (
            <p className="note">
              <button type="button" onClick={() => load()}>
                This column could not be read — try again
              </button>
            </p>
          ),
          Success: (pairs) =>
            pairs.length === 0 ? (
              <p className="note">Nothing passes the filters on this column</p>
            ) : (
              <>
                {pairs.map((pair) => (
                  <PulseV2Row
                    key={pair.address}
                    pair={pair}
                    statsPeriod={statsPeriod}
                  />
                ))}
              </>
            ),
        })}
      </div>
    </section>
  )
})

/**
 * How fast this column is filling, measured rather than guessed: the page the
 * feed just handed us spans a window, and the pools in it arrived across that
 * window. Too short a window says nothing, so it says nothing.
 */
const arrivalRate = (pairs: ReadonlyArray<PairModel>): string | undefined => {
  const times = pairs
    .map((pair) => pair.createdAt?.getTime())
    .filter((time): time is number => time !== undefined)

  if (times.length < 3) {
    return undefined
  }

  const minutes = (Math.max(...times) - Math.min(...times)) / 60_000
  if (minutes <= 0) {
    return undefined
  }

  const perMinute = times.length / minutes
  return perMinute >= 1
    ? `${Math.round(perMinute)}/min`
    : `${Math.round(perMinute * 60)}/h`
}

const CHAINS = [
  {
    label: 'Solana',
    color: '#14F195',
    tint: 'rgba(20,241,149,.16)',
    on: true,
    mark: (
      <>
        <path d="M7.2 5.2H19l-2.2 2.6H5z" />
        <path d="M5 10.7h11.8L19 13.3H7.2z" />
        <path d="M7.2 16.2H19l-2.2 2.6H5z" />
      </>
    ),
  },
  {
    label: 'Ethereum',
    color: '#627EEA',
    tint: 'rgba(98,126,234,.16)',
    on: false,
    mark: (
      <>
        <path d="M12 2.5 18 12l-6 3.4L6 12z" />
        <path d="M12 16.9 18 13.4 12 21.5 6 13.4z" />
      </>
    ),
  },
  {
    label: 'Base',
    color: '#4C86FF',
    tint: 'rgba(0,82,255,.16)',
    on: false,
    mark: (
      <path d="M12 3a9 9 0 1 0 0 18h8.6v-5.6H12a3.4 3.4 0 1 1 0-6.8h8.6V3z" />
    ),
  },
  {
    label: 'TON',
    color: '#0098EA',
    tint: 'rgba(0,152,234,.16)',
    on: false,
    mark: (
      <path d="M12 3.4 3.8 8.2 12 20.6l8.2-12.4zM12 6.6l4.6 2.7L12 16.4 7.4 9.3z" />
    ),
  },
  {
    label: 'BNB',
    color: '#F0B90B',
    tint: 'rgba(240,185,11,.16)',
    on: false,
    mark: (
      <>
        <path d="m12 3 2.6 2.6L12 8.2 9.4 5.6z" />
        <path d="m5.6 9.4 2.6 2.6-2.6 2.6L3 12z" />
        <path d="m18.4 9.4 2.6 2.6-2.6 2.6-2.6-2.6z" />
        <path d="m12 15.8 2.6 2.6L12 21l-2.6-2.6z" />
        <path d="m12 9.4 2.6 2.6-2.6 2.6L9.4 12z" />
      </>
    ),
  },
]

/**
 * The prototype was a fixed mock, so its list clipped. A live column has more
 * rows than fit, so it scrolls — the one behaviour the design could not state.
 */
const Screen = styled.div`
  position: fixed;
  inset: 0;

  /* No stacking order of its own. It had z-index 1, which put the board above
     every dialog the app mounts — they open, they are correctly themed, and
     they paint underneath. That is what "the menu does nothing" was: settings
     opened each time, 602 by 430 of it, behind the board. */

  ${pulseV2Styles}

  .list {
    overflow-y: auto;
    scrollbar-width: none;
  }

  .list::-webkit-scrollbar {
    display: none;
  }

  .note {
    margin: 0;
    display: grid;
    place-items: center;
    block-size: 100%;
    padding-inline: 32px;
    text-align: center;
    color: var(--t4);
    font-size: 11px;
    line-height: 16px;
  }

  /* The quick-buy amount is a number you click and type over, in the column
     head and in the footer both. Resting it looks exactly like the design's
     label-and-value; editing it keeps the same frame so nothing shifts. */
  .qb,
  .qbline {
    cursor: text;
  }

  .qb:hover,
  .qbline:hover .mono {
    color: var(--t1);
  }

  .qb:hover {
    border-color: var(--chip-hi);
  }

  .qbline {
    color: var(--t4);
    font-size: 11px;
    font-weight: 500;
  }

  .qbinput {
    border: 0;
    padding: 0;
    background: none;
    outline: none;
    font-size: 10.5px;
    font-weight: 600;
    text-align: right;
  }

  .fl.qbedit .qbinput {
    font-size: 11px;
  }

  .qbunit {
    color: var(--t5);
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.3px;
  }

  .qb.qbedit {
    border-color: var(--accent);
  }

  /* Columns share out the width that is actually there.
  
     The lifted stylesheet names a fixed number of tracks at each size, which
     was right while the board always showed its whole set: the breakpoints
     decided how many fitted and the template matched. Now a reader can turn a
     column off, and a fixed template left the survivors at a third of the
     screen with the rest of it black.
  
     Tracks are created by the children instead, so whatever is on screen
     divides the width between them — whether it was the reader who put a
     column away or a breakpoint that did. The cap on very wide screens stays:
     past 3300px a column stops growing at 700px and the set is centred, which
     is the whole point of that step. */
  .cols {
    grid-template-columns: none;
    grid-auto-flow: column;
    grid-auto-columns: minmax(0, 1fr);
  }

  @container pulse (min-width: 3300px) {
    .cols {
      grid-auto-columns: minmax(0, 700px);
    }
  }

  /* The token tile, and the two things that went wrong on it.

     It had no background of its own: the prototype gave every tile a painted
     tint, and when the real icon replaced that, nothing was left to cover the
     ring behind it. The bonding curve is drawn as a conic gradient on the
     wrapper and showed straight through, so a pool at a quarter of its curve
     wore a green quadrant across its face instead of a 2px arc.

     And this board is mounted outside the app's theme, carrying its own
     palette, so a shared component that reads the app's text tokens gets their
     light-theme values — the no-logo initial came out near-black on near-black.
     The tile hands those tokens this screen's tones. */
  .av {
    background: var(--chip);
    --text-5: var(--t2);
    --backgroundA-3: var(--chip);

    /* The tile carried its own inset outline, and the curve's ring sits just
       outside it. Two strokes a pixel apart read as one line plus a second
       that had come loose. The ring is the edge now, so there is only one. */
    box-shadow: none;
  }

  /* The ring is drawn over the wrapper's padding, outside the tile it circles. */
  .ring {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }

  /* The situational row states come from the design's second sheet, the one of
     row states rather than of the whole board, so their rules are not in the
     lifted stylesheet. Values are that sheet's, unchanged.

     The info token comes with them: the board's own palette never needed a
     blue and so does not define one. Without it the migrated flag's colour
     resolved to nothing and came out white — caught on staging, not in
     review. (No backticks in here: this is inside a template literal.) */
  .app {
    --info: #4ea7fa;
  }

  .rw {
    position: relative;
  }

  .edge {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 2px;
  }

  /* Density. Compact is the lifted stylesheet's own spacing, so only the
     roomier setting is written here: more air above and below a row, and a
     wider gap between its lines. Nothing is hidden or shown by density —
     that is what the field switches are for. */
  &[data-density='regular'] .rw,
  [data-density='regular'] .rw {
    padding: 14px 14px 13px;
    gap: 9px;
  }

  [data-density='regular'] .pills {
    height: 26px;
  }

  .flag {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    height: 16px;
    padding: 0 6px;
    border-radius: 9999px;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }
`
